/* Presentation only. No commerce, account, data-fetching or analytics code. */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 1024px)');
  const controllers = new Map();
  const motionAllowed = () => document.body.dataset.motion === 'true' && !reduced.matches;
  const revealObserver = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.remove('reveal-pending');
        revealObserver.unobserve(entry.target);
      }
    });
  }, { rootMargin: '0px 0px -80px 0px', threshold: 0 });
  const videoObserver = new IntersectionObserver(entries => {
    entries.forEach(({ target, isIntersecting }) => {
      target.dataset.visible = String(isIntersecting);
      syncVideo(target);
    });
  }, { threshold: .05 });

  function syncVideo(video) {
    const paused = video.closest('[data-paused="true"]');
    if (motionAllowed() && video.dataset.visible === 'true' && !paused) {
      video.play().catch(() => {}); // A poster remains when autoplay is denied.
    } else video.pause();
  }

  function initialize(root) {
    controllers.get(root)?.abort();
    const controller = new AbortController();
    controllers.set(root, controller);
    const { signal } = controller;
    root.querySelectorAll('[data-reveal]').forEach(el => {
      if (motionAllowed() && !window.Shopify?.designMode && el.getBoundingClientRect().top > innerHeight) {
        el.classList.add('reveal-pending');
        revealObserver.observe(el);
      } else el.classList.remove('reveal-pending');
    });
    root.querySelectorAll('video.media-video').forEach(video => {
      video.muted = true;
      video.playsInline = true;
      videoObserver.observe(video);
    });
    root.querySelectorAll('[data-motion-toggle]').forEach(button => {
      const scope = button.parentElement;
      const video = scope.querySelector('video');
      if (!video) return;
      button.hidden = false;
      const update = () => {
        const paused = motionAllowed() ? scope.dataset.paused === 'true' : video.paused;
        button.setAttribute('aria-pressed', String(paused));
        const label = paused ? button.dataset.playLabel : button.dataset.pauseLabel;
        button.setAttribute('aria-label', label);
        button.title = label;
        button.querySelector('[data-motion-pause]').hidden = paused;
        button.querySelector('[data-motion-play]').hidden = !paused;
      };
      button.addEventListener('click', () => {
        if (!motionAllowed()) {
          // Explicit playback remains possible with reduced motion; parallax stays disabled.
          if (video.paused) video.play().catch(() => {}); else video.pause();
          return;
        }
        scope.dataset.paused = String(scope.dataset.paused !== 'true');
        syncVideo(video);
        update();
      }, { signal });
      reduced.addEventListener('change', update, { signal });
      video.addEventListener('play', update, { signal });
      video.addEventListener('pause', update, { signal });
      update();
    });
    root.querySelectorAll('[data-hover-video]').forEach(card => {
      const video = card.querySelector('video.hover-video');
      if (!video) return;
      const play = () => { if (motionAllowed()) video.play().catch(() => {}); };
      const pause = () => video.pause();
      card.parentElement.addEventListener('pointerenter', play, { signal });
      card.parentElement.addEventListener('pointerleave', pause, { signal });
      card.parentElement.addEventListener('focusin', play, { signal });
      card.parentElement.addEventListener('focusout', pause, { signal });
    });

    const header = root.querySelector('[data-site-header]');
    if (header) {
      const toggle = header.querySelector('[data-menu-toggle]');
      const menu = header.querySelector('[data-mobile-menu]');
      const setMenu = open => {
        header.classList.toggle('is-open', open);
        menu.hidden = !open;
        toggle.setAttribute('aria-expanded', String(open));
        toggle.querySelector('[data-menu-open]').hidden = open;
        toggle.querySelector('[data-menu-close]').hidden = !open;
      };
      toggle.addEventListener('click', () => setMenu(toggle.getAttribute('aria-expanded') !== 'true'), { signal });
      menu.addEventListener('click', e => { if (e.target.closest('a')) setMenu(false); }, { signal });
      header.addEventListener('keydown', e => {
        if (e.key === 'Escape') { setMenu(false); header.querySelectorAll('details[open]').forEach(d => d.open = false); toggle.focus(); }
      }, { signal });
      desktop.addEventListener('change', () => { if (desktop.matches) setMenu(false); }, { signal });
    }
    updateScroll();
  }

  let scheduled = false;
  function updateScroll() {
    document.querySelectorAll('[data-site-header]').forEach(header => {
      const heroFirst = document.querySelector('#MainContent > .shopify-section:first-child [data-hero]');
      header.classList.toggle('is-solid', scrollY > 40 || !heroFirst);
    });
    document.querySelectorAll('[data-parallax]').forEach(el => {
      const y = motionAllowed() && !el.closest('[data-paused="true"]') ? Math.min(scrollY, 900) * (120 / 900) : 0;
      el.style.transform = y ? `translateY(${y}px)` : '';
    });
    scheduled = false;
  }
  window.addEventListener('scroll', () => {
    if (!scheduled) { scheduled = true; requestAnimationFrame(updateScroll); }
  }, { passive: true });
  reduced.addEventListener('change', () => {
    document.querySelectorAll('[data-reveal]').forEach(el => el.classList.remove('reveal-pending'));
    document.querySelectorAll('video').forEach(video => { if (!motionAllowed()) video.pause(); else if (video.classList.contains('media-video')) syncVideo(video); });
    updateScroll();
  });
  document.querySelectorAll('.shopify-section').forEach(initialize);
  document.addEventListener('shopify:section:load', e => initialize(e.target));
  document.addEventListener('shopify:section:unload', e => {
    controllers.get(e.target)?.abort();
    controllers.delete(e.target);
    e.target.querySelectorAll('[data-reveal]').forEach(el => revealObserver.unobserve(el));
    e.target.querySelectorAll('video').forEach(video => { videoObserver.unobserve(video); video.pause(); });
  });
  document.addEventListener('shopify:section:reorder', updateScroll);
  document.addEventListener('shopify:block:select', e => e.target.closest('[data-reveal]')?.classList.remove('reveal-pending'));
})();
